import React, { useState, useEffect, useRef } from 'react';
import { GoogleOAuthProvider } from '@react-oauth/google';
import SocialAuthButtons from './SocialAuthButtons';
import VoxSmythIcons, { VoxSmythAnimations } from './VoxSmythIcons';
import TurnstileWidget from './components/TurnstileWidget';
import tokenService from './services/tokenService';
import { extractErrorMessage } from './utils/extractErrorMessage';
import './VoxSmythIcons.css';

// Enhanced Auth Page - "Literary Gateway" themed
const EnhancedAuthPage = ({ 
  onNavigateHome,
  onSuccessfulAuth 
}) => {
  // Check URL parameters for mode
  const urlParams = new URLSearchParams(window.location.search);
  const initialMode = urlParams.get('mode') === 'signup' ? false : true;
  
  const [isLogin, setIsLogin] = useState(initialMode);
  const [showPassword, setShowPassword] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const turnstileRef = useRef(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: ''
  });
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Enhanced Design Tokens for Auth
  const AUTH_TOKENS = {
    colors: {
      // Core Palette
      slate: '#2C3A4C',
      antique_gold: '#D7B377',
      warm_parchment: '#F9F6F0',
      
      // Auth-specific
      gateway_dark: '#1A202C',
      literary_gold: '#DAA520',
      wisdom_purple: '#8E44AD',
      trust_blue: '#3498DB',
      error_red: '#E74C3C',
      success_green: '#27AE60'
    },
    typography: {
      main_title: 'DM Serif Display, Georgia, serif',
      section_title: 'DM Serif Display, Georgia, serif',
      label: 'DM Sans, sans-serif',
      input: 'Inter, sans-serif',
      button: 'DM Sans, sans-serif'
    },
    layout: {
      form_width: '420px',
      input_height: '48px'
    }
  };

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    console.log('🔐 Form submission started...', { 
      email: formData.email, 
      isLogin, 
      endpoint: isLogin ? '/api/login' : '/api/signup' 
    });

    try {
      const endpoint = isLogin ? '/api/login' : '/api/signup';
      const payload = isLogin 
        ? { email: formData.email, password: formData.password, turnstile_token: turnstileToken, remember_me: rememberMe }
        : { ...formData, turnstile_token: turnstileToken };

      console.log('📡 Making API request to:', `${BACKEND_URL}${endpoint}`);

      const response = await fetch(`${BACKEND_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',  // Receive httpOnly auth cookie (Phase 1 cookie migration)
        body: JSON.stringify(payload)
      });

      console.log('📡 Response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Authentication successful:', { 
          hasToken: !!(data.token || data.access_token),
          userTier: data.user?.tier,
          userEmail: data.user?.email
        });
        
        const token = data.token || data.access_token;
        if (token) {
          // Backend already set the httpOnly auth cookie. setToken is now a
          // no-op kept for legacy call sites — see services/tokenService.js.
          tokenService.setToken(token);
          console.log('✅ Cookie session established (httpOnly)');
          
          // Clear voice actor status cache on login to ensure fresh check
          sessionStorage.removeItem('voice-actor-status');
          sessionStorage.removeItem('voice-actor-check-time');
          console.log('🔄 Cleared voice actor status cache for fresh check');
          
          // Call the success handler
          if (onSuccessfulAuth) {
            console.log('🔄 Calling onSuccessfulAuth...');
            onSuccessfulAuth(data);
          } else {
            console.warn('⚠️ No onSuccessfulAuth handler provided');
          }
        } else {
          console.error('❌ No token in response');
          setError('Authentication failed - no token received');
        }
      } else {
        const error = await response.json();
        console.error('❌ Authentication failed:', error);
        setError(extractErrorMessage(error, `${isLogin ? 'Login' : 'Registration'} failed`));
        // Reset Turnstile for retry (tokens are single-use)
        setTurnstileToken(null);
        if (turnstileRef.current) turnstileRef.current.reset();
      }
    } catch (error) {
      console.error('❌ Network error:', error);
      setError('Network error occurred');
      setTurnstileToken(null);
      if (turnstileRef.current) turnstileRef.current.reset();
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) setError(''); // Clear error when user types
  };

  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || ''}>
      <div 
        className="enhanced-auth-page"
        style={{
          minHeight: '100vh',
          background: `linear-gradient(135deg, ${AUTH_TOKENS.colors.gateway_dark} 0%, ${AUTH_TOKENS.colors.slate} 100%)`,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Global Styles */}
        <style>{`
          ${VoxSmythAnimations}
          
          .auth-form-input:focus {
            outline: none;
            border-color: ${AUTH_TOKENS.colors.literary_gold};
            box-shadow: 0 0 0 3px ${AUTH_TOKENS.colors.literary_gold}20;
          }
        `}</style>

        {/* Background Pattern */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: `
              radial-gradient(circle at 25% 25%, ${AUTH_TOKENS.colors.literary_gold}12 0%, transparent 50%),
              radial-gradient(circle at 75% 75%, ${AUTH_TOKENS.colors.wisdom_purple}08 0%, transparent 50%)
            `,
            pointerEvents: 'none'
          }}
        />

        {/* Navigation */}
        <nav style={{ 
          position: 'absolute', 
          top: window.innerWidth < 768 ? '16px' : '24px', 
          left: window.innerWidth < 768 ? '16px' : '24px',
          zIndex: 10
        }}>
          <button
            onClick={onNavigateHome}
            style={{
              background: 'transparent',
              border: `1px solid ${AUTH_TOKENS.colors.antique_gold}60`,
              borderRadius: '8px',
              color: 'white',
              padding: window.innerWidth < 768 ? '8px 16px' : '12px 20px',
              fontSize: window.innerWidth < 768 ? '12px' : '14px',
              fontFamily: AUTH_TOKENS.typography.button,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.borderColor = AUTH_TOKENS.colors.antique_gold;
              e.target.style.background = `${AUTH_TOKENS.colors.antique_gold}15`;
            }}
            onMouseLeave={(e) => {
              e.target.style.borderColor = `${AUTH_TOKENS.colors.antique_gold}60`;
              e.target.style.background = 'transparent';
            }}
          >
            <VoxSmythIcons.ArrowLeft size={14} color={AUTH_TOKENS.colors.antique_gold} />
            Back to Home
          </button>
        </nav>

        {/* Main Content */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          padding: window.innerWidth < 768 ? '20px 16px' : '40px 24px'
        }}>
          <div
            style={{
              background: 'rgba(255, 255, 255, 0.95)',
              borderRadius: '20px',
              padding: window.innerWidth < 768 ? '32px 24px' : '48px',
              width: '100%',
              maxWidth: window.innerWidth < 768 ? '100%' : AUTH_TOKENS.layout.form_width,
              boxShadow: '0 20px 60px rgba(26, 32, 44, 0.3)',
              border: `1px solid ${AUTH_TOKENS.colors.antique_gold}20`
            }}
          >
            {/* Header */}
            <div style={{ textAlign: 'center', marginBottom: window.innerWidth < 768 ? '24px' : '32px' }}>
              <div style={{ marginBottom: '16px' }}>
                <VoxSmythIcons.Quill size={window.innerWidth < 768 ? 40 : 48} color={AUTH_TOKENS.colors.literary_gold} />
              </div>
              
              <h1
                style={{
                  fontFamily: AUTH_TOKENS.typography.main_title,
                  fontSize: window.innerWidth < 768 ? '24px' : '32px',
                  color: AUTH_TOKENS.colors.slate,
                  margin: '0 0 8px 0'
                }}
              >
                {isLogin ? 'Welcome Back' : 'Join VoxSmyth'}
              </h1>
              
              <p
                style={{
                  fontFamily: AUTH_TOKENS.typography.input,
                  fontSize: window.innerWidth < 768 ? '14px' : '16px',
                  color: `${AUTH_TOKENS.colors.slate}80`,
                  margin: 0
                }}
              >
                {isLogin ? 'Enter your literary sanctuary' : 'Begin your storytelling journey'}
              </p>
            </div>

            {/* Toggle Buttons */}
            <div
              style={{
                display: 'flex',
                background: `${AUTH_TOKENS.colors.slate}10`,
                borderRadius: '12px',
                padding: '4px',
                marginBottom: '24px'
              }}
            >
              <button
                onClick={() => {
                  setIsLogin(true);
                  setError('');
                  setFormData({ email: '', password: '', firstName: '', lastName: '' });
                }}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: isLogin ? AUTH_TOKENS.colors.literary_gold : 'transparent',
                  color: isLogin ? 'white' : AUTH_TOKENS.colors.slate,
                  border: 'none',
                  borderRadius: '8px',
                  fontFamily: AUTH_TOKENS.typography.button,
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
              >
                Sign In
              </button>
              <button
                onClick={() => {
                  setIsLogin(false);
                  setError('');
                  setFormData({ email: '', password: '', firstName: '', lastName: '' });
                }}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: !isLogin ? AUTH_TOKENS.colors.literary_gold : 'transparent',
                  color: !isLogin ? 'white' : AUTH_TOKENS.colors.slate,
                  border: 'none',
                  borderRadius: '8px',
                  fontFamily: AUTH_TOKENS.typography.button,
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
              >
                Create Account
              </button>
            </div>

            {/* Error Message */}
            {error && (
              <div
                style={{
                  background: `${AUTH_TOKENS.colors.error_red}10`,
                  border: `1px solid ${AUTH_TOKENS.colors.error_red}30`,
                  borderRadius: '8px',
                  padding: '12px 16px',
                  marginBottom: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <VoxSmythIcons.Warning size={16} color={AUTH_TOKENS.colors.error_red} />
                <span
                  style={{
                    fontFamily: AUTH_TOKENS.typography.input,
                    fontSize: '14px',
                    color: AUTH_TOKENS.colors.error_red,
                    fontWeight: '500'
                  }}
                >
                  {error}
                </span>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit}>
              {!isLogin && (
                <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
                  <div style={{ flex: 1 }}>
                    <label
                      style={{
                        display: 'block',
                        fontFamily: AUTH_TOKENS.typography.label,
                        fontSize: '14px',
                        fontWeight: '500',
                        color: AUTH_TOKENS.colors.slate,
                        marginBottom: '6px'
                      }}
                    >
                      First Name
                    </label>
                    <input
                      type="text"
                      className="auth-form-input"
                      value={formData.firstName}
                      onChange={(e) => handleInputChange('firstName', e.target.value)}
                      required={!isLogin}
                      style={{
                        width: '100%',
                        height: AUTH_TOKENS.layout.input_height,
                        padding: '0 16px',
                        border: `1px solid ${AUTH_TOKENS.colors.slate}30`,
                        borderRadius: '8px',
                        fontSize: '16px',
                        fontFamily: AUTH_TOKENS.typography.input,
                        color: AUTH_TOKENS.colors.slate,
                        backgroundColor: 'white',
                        transition: 'all 0.3s ease'
                      }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label
                      style={{
                        display: 'block',
                        fontFamily: AUTH_TOKENS.typography.label,
                        fontSize: '14px',
                        fontWeight: '500',
                        color: AUTH_TOKENS.colors.slate,
                        marginBottom: '6px'
                      }}
                    >
                      Last Name
                    </label>
                    <input
                      type="text"
                      className="auth-form-input"
                      value={formData.lastName}
                      onChange={(e) => handleInputChange('lastName', e.target.value)}
                      required={!isLogin}
                      style={{
                        width: '100%',
                        height: AUTH_TOKENS.layout.input_height,
                        padding: '0 16px',
                        border: `1px solid ${AUTH_TOKENS.colors.slate}30`,
                        borderRadius: '8px',
                        fontSize: '16px',
                        fontFamily: AUTH_TOKENS.typography.input,
                        color: AUTH_TOKENS.colors.slate,
                        backgroundColor: 'white',
                        transition: 'all 0.3s ease'
                      }}
                    />
                  </div>
                </div>
              )}

              <div style={{ marginBottom: '20px' }}>
                <label
                  style={{
                    display: 'block',
                    fontFamily: AUTH_TOKENS.typography.label,
                    fontSize: '14px',
                    fontWeight: '500',
                    color: AUTH_TOKENS.colors.slate,
                    marginBottom: '6px'
                  }}
                >
                  Email Address
                </label>
                <input
                  type="email"
                  className="auth-form-input"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  required
                  style={{
                    width: '100%',
                    height: AUTH_TOKENS.layout.input_height,
                    padding: '0 16px',
                    border: `1px solid ${AUTH_TOKENS.colors.slate}30`,
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontFamily: AUTH_TOKENS.typography.input,
                    color: AUTH_TOKENS.colors.slate,
                    backgroundColor: 'white',
                    transition: 'all 0.3s ease'
                  }}
                />
              </div>

              {/* Password Field */}
              <div style={{ marginBottom: '24px' }}>
                <label
                  htmlFor="password"
                  style={{
                    display: 'block',
                    fontFamily: AUTH_TOKENS.typography.label,
                    fontSize: '14px',
                    fontWeight: '500',
                    color: AUTH_TOKENS.colors.slate,
                    marginBottom: '6px'
                  }}
                >
                  Password
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPassword ? "text" : "password"}
                    className="auth-form-input"
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    required
                    style={{
                      width: '100%',
                      height: AUTH_TOKENS.layout.input_height,
                      padding: '0 48px 0 16px', // Extra padding for eye icon
                      border: `1px solid ${AUTH_TOKENS.colors.slate}30`,
                      borderRadius: '8px',
                      fontSize: '16px',
                      fontFamily: AUTH_TOKENS.typography.input,
                      color: AUTH_TOKENS.colors.slate,
                      backgroundColor: 'white',
                      transition: 'all 0.3s ease'
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      border: 'none',
                      background: 'transparent',
                      cursor: 'pointer',
                      color: AUTH_TOKENS.colors.slate,
                      fontSize: '18px',
                      padding: '0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    title={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>
              </div>

              {/* Turnstile Bot Protection */}
              <TurnstileWidget
                ref={turnstileRef}
                onVerify={setTurnstileToken}
                theme="light"
              />

              {/* Remember me for 30 days — login only */}
              {isLogin && (
                <label
                  htmlFor="remember-me-checkbox"
                  data-testid="remember-me-row"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    marginBottom: '16px',
                    cursor: 'pointer',
                    fontFamily: AUTH_TOKENS.typography.input,
                    fontSize: '14px',
                    color: AUTH_TOKENS.colors.slate,
                    userSelect: 'none',
                  }}
                >
                  <input
                    id="remember-me-checkbox"
                    data-testid="remember-me-checkbox"
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    style={{
                      width: '16px',
                      height: '16px',
                      accentColor: AUTH_TOKENS.colors.literary_gold,
                      cursor: 'pointer',
                    }}
                  />
                  <span>Keep me signed in for 30 days</span>
                </label>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                style={{
                  width: '100%',
                  height: '48px',
                  background: loading 
                    ? `${AUTH_TOKENS.colors.literary_gold}60` 
                    : `linear-gradient(135deg, ${AUTH_TOKENS.colors.literary_gold} 0%, ${AUTH_TOKENS.colors.antique_gold} 100%)`,
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontFamily: AUTH_TOKENS.typography.button,
                  fontWeight: '600',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  marginBottom: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => {
                  if (!loading) {
                    e.target.style.transform = 'translateY(-1px)';
                    e.target.style.boxShadow = `0 6px 20px ${AUTH_TOKENS.colors.literary_gold}30`;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!loading) {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = 'none';
                  }
                }}
              >
                {loading ? (
                  <>
                    <VoxSmythIcons.Loading size={16} color="white" />
                    {isLogin ? 'Signing In...' : 'Creating Account...'}
                  </>
                ) : (
                  <>
                    <VoxSmythIcons.User size={16} color="white" />
                    {isLogin ? 'Sign In' : 'Create Account'}
                  </>
                )}
              </button>

              {/* Forgot Password Link - Only show on login */}
              {isLogin && (
                <div style={{ textAlign: 'right', marginBottom: '20px' }}>
                  <button
                    type="button"
                    onClick={() => window.location.href = '/forgot-password'}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: AUTH_TOKENS.colors.literary_gold,
                      fontSize: '14px',
                      fontFamily: AUTH_TOKENS.typography.input,
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      padding: '0'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.color = AUTH_TOKENS.colors.antique_gold;
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.color = AUTH_TOKENS.colors.literary_gold;
                    }}
                  >
                    Forgot Password?
                  </button>
                </div>
              )}
            </form>

            {/* Social Login */}
            <div style={{ textAlign: 'center' }}>
              <div
                style={{
                  marginBottom: '20px',
                  position: 'relative'
                }}
              >
                <div
                  style={{
                    height: '1px',
                    background: `${AUTH_TOKENS.colors.slate}20`,
                    position: 'relative'
                  }}
                />
                <span
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    background: 'white',
                    padding: '0 16px',
                    fontSize: '14px',
                    color: `${AUTH_TOKENS.colors.slate}60`,
                    fontFamily: AUTH_TOKENS.typography.input
                  }}
                >
                  or continue with
                </span>
              </div>

              <SocialAuthButtons 
                onSuccess={onSuccessfulAuth}
                onError={(error) => {
                  console.error('OAuth error:', error);
                  setError(error);
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </GoogleOAuthProvider>
  );
};

export default EnhancedAuthPage;